import { apiCall } from '../api';
import { getUser } from '../auth';

// Types
export interface Notice {
  id: string;
  recipient_username: string;
  subject: string;
  body: string;
  priority: string;
  source?: string;
  created_at: string;
  read_at?: string | null;
  expires_at?: string | null;
  metadata?: Record<string, unknown>;
}

export interface UnreadNoticesResponse {
  unread_count: number;
  notices: Notice[];
}

export interface UserNoticesResponse {
  notices: Notice[];
  pagination_key?: string | null;
  has_more: boolean;
}

export interface NoticeResponse {
  id: string;
  recipient_username: string;
  subject: string;
  body: string;
  priority: string;
  source?: string;
  created_at: string;
  read_at?: string | null;
  expires_at?: string | null;
  metadata?: Record<string, unknown>;
}

export interface MarkAllReadResponse {
  updated_count: number;
}

// Cache for unread count
let unreadCountCache: { count: number; timestamp: number } | null = null;
const CACHE_TTL = 30000; // 30 seconds

// API functions

/**
 * Get unread notices and count
 */
export async function loadUnreadNotices(): Promise<UnreadNoticesResponse> {
  try {
    const response = await apiCall<UnreadNoticesResponse>('GET', '/api/notices/unread');
    // Update cache
    unreadCountCache = {
      count: response.unread_count,
      timestamp: Date.now(),
    };
    return response;
  } catch (error) {
    console.error('Error loading unread notices:', error);
    throw error;
  }
}

/**
 * Get paginated notices for the current user
 */
export async function loadNotices(limit = 20, paginationKey?: string | null): Promise<UserNoticesResponse> {
  try {
    const params: Record<string, string | number> = { limit };
    if (paginationKey) {
      params.pagination_key = paginationKey;
    }
    
    const response = await apiCall<UserNoticesResponse>('GET', '/api/notices/', { params });
    return response;
  } catch (error) {
    console.error('Error loading notices:', error);
    throw error;
  }
}

/**
 * Get a specific notice
 */
export async function getNotice(noticeId: string): Promise<NoticeResponse> {
  try {
    const response = await apiCall<NoticeResponse>('GET', `/api/notices/${noticeId}`);
    // Invalidate cache since getting a notice marks it as read
    unreadCountCache = null;
    return response;
  } catch (error) {
    console.error('Error getting notice:', error);
    throw error;
  }
}

/**
 * Mark a notice as read
 */
export async function markNoticeAsRead(noticeId: string): Promise<NoticeResponse> {
  try {
    const response = await apiCall<NoticeResponse>('PATCH', `/api/notices/${noticeId}/read`);
    // Invalidate cache
    unreadCountCache = null;
    return response;
  } catch (error) {
    console.error('Error marking notice as read:', error);
    throw error;
  }
}

/**
 * Mark a notice as unread
 */
export async function markNoticeAsUnread(noticeId: string): Promise<NoticeResponse> {
  try {
    const response = await apiCall<NoticeResponse>('PATCH', `/api/notices/${noticeId}/unread`);
    // Invalidate cache
    unreadCountCache = null;
    return response;
  } catch (error) {
    console.error('Error marking notice as unread:', error);
    throw error;
  }
}

/**
 * Mark all notices as read
 */
export async function markAllNoticesAsRead(): Promise<MarkAllReadResponse> {
  try {
    const response = await apiCall<MarkAllReadResponse>('POST', '/api/notices/mark-all-read');
    // Invalidate cache
    unreadCountCache = null;
    return response;
  } catch (error) {
    console.error('Error marking all notices as read:', error);
    throw error;
  }
}

/**
 * Delete a notice
 */
export async function deleteNotice(noticeId: string): Promise<void> {
  try {
    await apiCall('DELETE', `/api/notices/${noticeId}`);
    // Invalidate cache
    unreadCountCache = null;
  } catch (error) {
    console.error('Error deleting notice:', error);
    throw error;
  }
}

/**
 * Check for unread notices (cached)
 * This is used by the header badge to avoid constant API calls
 */
export async function checkUnreadNotices(): Promise<number> {
  // Check cache first
  if (unreadCountCache && (Date.now() - unreadCountCache.timestamp) < CACHE_TTL) {
    return unreadCountCache.count;
  }
  
  try {
    const response = await loadUnreadNotices();
    return response.unread_count;
  } catch (error) {
    console.error('Error checking unread notices:', error);
    return 0; // Return 0 on error to avoid breaking the UI
  }
}

/**
 * Update the inbox badge in the header
 */
export function updateInboxBadge(count: number): void {
  const badge = document.getElementById('inbox-badge');
  if (badge) {
    if (count > 0) {
      badge.textContent = count.toString();
      badge.style.display = 'inline-flex';
    } else {
      badge.style.display = 'none';
    }
  }
}

/**
 * Initialize notices checking
 * This sets up periodic checking for unread notices
 */
export async function initializeNoticesChecking(): Promise<void> {
  // Initial check
  const count = await checkUnreadNotices();
  updateInboxBadge(count);
  
  // Set up periodic checking (every 60 seconds)
  window.setInterval(async () => {
    const count = await checkUnreadNotices();
    updateInboxBadge(count);
  }, 60000);
  
  // Also check when window regains focus
  document.addEventListener('visibilitychange', async () => {
    if (!document.hidden) {
      const count = await checkUnreadNotices();
      updateInboxBadge(count);
    }
  });
  
  // Listen for auth state changes
  window.addEventListener('auth-state-changed', async () => {
    // Clear cache on auth change
    unreadCountCache = null;
    const user = await getUser();
    if (user) {
      const count = await checkUnreadNotices();
      updateInboxBadge(count);
    } else {
      updateInboxBadge(0);
    }
  });
}